import axios from "axios";

import { hideLoader, showLoader } from "../utils/loader";
import {
  clearActiveAuthSession,
  persistActiveAuthTokens,
} from "../utils/storageUtils";
import {
  isExpiredSubscriptionResponse,
  subscriptionManager,
} from "../utils/subscriptionManager";
import { refreshToken } from "./apiAuth";
import {
  getAttemptedUrl,
  getRefreshTokenFromResponse,
  setTokenHeader,
} from "./axiosClientUtils";

export function createApiClient({ baseURL, getToken, getRefreshToken }) {
  const client = axios.create({
    baseURL: baseURL || "",
  });

  client.interceptors.request.use((request) => {
    setTokenHeader(request.headers, getToken());
    return request;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (isExpiredSubscriptionResponse(error)) {
        subscriptionManager.notifyExpired();
        return Promise.reject(error);
      }

      if (error?.response?.status === 401) {
        if (!error.config || error.config._retry) {
          return Promise.reject(error);
        }

        showLoader();
        error.config._retry = true;

        try {
          const refresh_token = getRefreshToken();

          if (!refresh_token) {
            throw new Error("No refresh token");
          }

          const response = await refreshToken(refresh_token);
          const token = getRefreshTokenFromResponse(response);

          persistActiveAuthTokens({
            token: token?.access_token,
            refresh_token: token?.refresh_token,
          });

          setTokenHeader(error.config.headers, token?.access_token);

          hideLoader();
          return client.request(error.config);
        } catch (refreshError) {
          hideLoader();
          clearActiveAuthSession();
          const attemptedUrl = getAttemptedUrl(window.location);
          window.location = `${window.location.origin}?url=${attemptedUrl}`;
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
}
